import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Box,
    Switch,
    FormControlLabel,
    Tooltip,
    IconButton,
    Collapse,
    Alert,
    CircularProgress,
    Chip,
    OutlinedInput,
    SelectChangeEvent,
    Tabs,
    Tab,
    Paper,
    Divider
} from '@mui/material';
import {
    Settings as SettingsIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Info as InfoIcon,
    BookOnline as BookingsIcon,
    Assignment as OrdersIcon
} from '@mui/icons-material';
import { bookingService } from '../services/bookingService';
import apiClient from '../services/apiClient';
import BookingPreviewDialog from './BookingPreviewDialog';

interface Diary {
    id: string;
    code: string;
    name: string;
    description?: string;
    execution_unit_name?: string;
    healthcare_service_name?: string;
}

interface SyncOptions {
    syncDaysBack?: number;
    syncDaysForward?: number;
    fromDate?: string;
    toDate?: string;
    fromTime?: string;
    toTime?: string;
    batchSize?: number;
    maxRetries?: number;
    retryDelay?: number;
    timeout?: number;
    method?: 'all' | 'prescription' | 'patient' | 'specific' | 'diary';
    patientIds?: string[];
    bookingIds?: string[];
    diaryIds?: string[];
    authorityId?: string;
    forceSync?: boolean;
    dryRun?: boolean;
    // New API endpoint selection
    apiEndpoint?: 'bookings' | 'orders';
    // Orders-specific parameters
    orderStatus?: string;
    serviceType?: string;
    executionUnitId?: string;
}

interface SyncConfigDialogProps {
    open: boolean;
    onClose: () => void;
    onSync: (options: SyncOptions) => void;
    loading: boolean;
}

const SyncConfigDialog: React.FC<SyncConfigDialogProps> = ({ 
    open, 
    onClose, 
    onSync, 
    loading 
}) => {
    const [options, setOptions] = useState<SyncOptions>({
        syncDaysBack: 30,
        syncDaysForward: 90,
        // Inizializza range date di default: 30 giorni indietro, 90 giorni avanti
        fromDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        toDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        fromTime: '00:00',
        toTime: '23:59',
        batchSize: 50,
        maxRetries: 3,
        retryDelay: 1000,
        timeout: 600000, // 10 minuti
        method: 'diary', // Predefinito su selezione agenda
        forceSync: false,
        dryRun: false,
        apiEndpoint: 'bookings' // Predefinito su bookings
    });

    const [activeTab, setActiveTab] = useState(0); // 0 = bookings, 1 = orders
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [patientIdsText, setPatientIdsText] = useState('');
    const [bookingIdsText, setBookingIdsText] = useState('');
    
    // Stati per gestione agende
    const [diaries, setDiaries] = useState<Diary[]>([]);
    const [selectedDiaryIds, setSelectedDiaryIds] = useState<string[]>([]);
    const [loadingDiaries, setLoadingDiaries] = useState(false);
    const [diariesError, setDiariesError] = useState<string | null>(null);
    
    // Stati per modale di anteprima
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);
    const [previewOptions, setPreviewOptions] = useState<SyncOptions | null>(null);

    // Carica le agende disponibili quando il dialog si apre
    useEffect(() => {
        if (open) {
            loadDiaries();
        }
    }, [open]);

    const loadDiaries = async () => {
        setLoadingDiaries(true);
        setDiariesError(null);
        try {
            console.log('Caricamento agende...');
            
            // Verifica se il token esiste
            const token = localStorage.getItem('authToken');
            if (!token) {
                setDiariesError('Token di autenticazione non trovato. Effettua nuovamente il login.');
                setLoadingDiaries(false);
                return;
            }
            
            console.log('Token presente, lunghezza:', token.length);
            
            // Usiamo l'apiClient esistente che gestisce automaticamente l'autenticazione
            const response = await apiClient.get('/diaries');
            
            console.log('Risposta API agende:', response.data);
            
            if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data)) {
                // Trasformiamo i dati nel formato atteso
                const transformedDiaries = response.data.data
                    .filter((diary: any) => diary && !diary.is_deleted) // Filtra agende non eliminate
                    .map((diary: any) => ({
                        id: diary.id,
                        code: diary.code,
                        name: diary.name,
                        description: diary.description,
                        execution_unit_name: diary.execution_unit_name,
                        healthcare_service_name: diary.healthcare_service_name || 'N/A'
                    }));
                
                setDiaries(transformedDiaries);
                console.log('Agende caricate:', transformedDiaries.length, 'prime 2:', transformedDiaries.slice(0, 2));
                
                if (transformedDiaries.length === 0) {
                    setDiariesError('Nessuna agenda attiva trovata. Sincronizza prima le agende dal sistema RECUP.');
                }
            } else {
                console.error('Formato risposta non valido:', response.data);
                setDiariesError('Formato risposta non valido dal server');
            }
        } catch (error: any) {
            console.error('Errore nel caricamento agende:', error);
            
            if (error.response?.status === 401) {
                setDiariesError('Sessione scaduta. Effettua nuovamente il login.');
            } else if (error.response?.status === 403) {
                setDiariesError('Non hai i permessi per accedere alle agende.');
            } else if (error.response?.status) {
                setDiariesError(`Errore server: HTTP ${error.response.status}`);
            } else {
                setDiariesError('Errore di connessione: ' + error.message);
            }
        } finally {
            setLoadingDiaries(false);
        }
    };

    const handleDiarySelectionChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        const diaryIds = typeof value === 'string' ? value.split(',') : value;
        setSelectedDiaryIds(diaryIds);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        const newApiEndpoint = newValue === 0 ? 'bookings' : 'orders';
        setOptions(prev => ({
            ...prev,
            apiEndpoint: newApiEndpoint
        }));
    };

    const handleOptionChange = (field: keyof SyncOptions, value: any) => {
        setOptions(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSync = () => {
        // Validazione: se metodo è "diary", deve essere selezionata almeno un'agenda
        if (options.method === 'diary' && selectedDiaryIds.length === 0) {
            // Mostra errore - puoi usare un alert o un toast
            alert('ERRORE: Devi selezionare almeno un\'agenda quando usi il metodo "Per agenda specifica".\n\nSeleziona una o più agende dalla lista o cambia metodo di sincronizzazione.');
            return;
        }

        // Validazione: se metodo è "patient", deve essere specificato almeno un paziente
        if (options.method === 'patient' && (!patientIdsText || patientIdsText.trim().length === 0)) {
            alert('ERRORE: Devi specificare almeno un codice fiscale paziente quando usi il metodo "Per paziente specifico".\n\nInserisci i codici fiscali nel campo dedicato.');
            return;
        }

        // Validazione: se metodo è "specific", devono essere specificati booking IDs
        if (options.method === 'specific' && (!bookingIdsText || bookingIdsText.trim().length === 0)) {
            alert('ERRORE: Devi specificare almeno un ID prenotazione quando usi il metodo "Appuntamenti specifici".\n\nInserisci gli ID delle prenotazioni nel campo dedicato.');
            return;
        }

        // Costruisci datetime combinando data e ora
        const fromDateTime = `${options.fromDate}T${options.fromTime}:00`;
        const toDateTime = `${options.toDate}T${options.toTime}:59`;

        const finalOptions = {
            ...options,
            // Usa le date e ore specificate dall'utente
            fromDate: fromDateTime,
            toDate: toDateTime,
            patientIds: patientIdsText ? patientIdsText.split(',').map(id => id.trim()).filter(id => id) : undefined,
            bookingIds: bookingIdsText ? bookingIdsText.split(',').map(id => id.trim()).filter(id => id) : undefined,
            diaryIds: options.method === 'diary' && selectedDiaryIds.length > 0 ? selectedDiaryIds : undefined
        };

        // Apri la modale di anteprima invece di sincronizzare direttamente
        setPreviewOptions(finalOptions);
        setShowPreviewDialog(true);
    };

    const handleConfirmSync = (selectedBookings: string[], overwriteExisting: boolean) => {
        if (!previewOptions) return;

        const finalOptions = {
            ...previewOptions,
            bookingIds: selectedBookings,
            force_overwrite: overwriteExisting
        };

        // Chiudi le modali e avvia la sincronizzazione
        setShowPreviewDialog(false);
        onClose(); // Chiudi anche la modale di configurazione
        onSync(finalOptions);
    };

    const handleClosePreview = () => {
        setShowPreviewDialog(false);
        setPreviewOptions(null);
    };

    const resetDefaults = () => {
        setOptions({
            syncDaysBack: 30,
            syncDaysForward: 90,
            // Reset range date di default: 30 giorni indietro, 90 giorni avanti
            fromDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            toDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            fromTime: '00:00',
            toTime: '23:59',
            batchSize: 50,
            maxRetries: 3,
            retryDelay: 1000,
            timeout: 600000,
            method: 'diary', // Predefinito su selezione agenda
            forceSync: false,
            dryRun: false,
            apiEndpoint: 'bookings' // Reset to bookings
        });
        setActiveTab(0); // Reset to bookings tab
        setPatientIdsText('');
        setBookingIdsText('');
        setSelectedDiaryIds([]);
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: { minHeight: '500px' }
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <SettingsIcon />
                    Configurazione Sincronizzazione RECUP
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {/* Tabs per selezione servizio */}
                <Paper sx={{ mt: 1, mb: 3 }}>
                    <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                        <Tab 
                            icon={<BookingsIcon />} 
                            label="Prenotazioni (Bookings)" 
                            sx={{ minHeight: 48 }}
                        />
                        <Tab 
                            icon={<OrdersIcon />} 
                            label="Ordini (Orders)" 
                            sx={{ minHeight: 48 }}
                        />
                    </Tabs>
                    
                    <Box sx={{ p: 2 }}>
                        {activeTab === 0 && (
                            <Alert severity="info" variant="outlined">
                                <Typography variant="body2">
                                    <strong>Servizio Prenotazioni:</strong> Utilizza l'endpoint <code>/api/v3/system-apis/bookings</code> 
                                    per sincronizzare le prenotazioni sanitarie dal sistema RECUP.
                                </Typography>
                            </Alert>
                        )}
                        
                        {activeTab === 1 && (
                            <Alert severity="info" variant="outlined">
                                <Typography variant="body2">
                                    <strong>Servizio Ordini:</strong> Utilizza l'endpoint <code>/api/v3/system-apis/orders</code> 
                                    per sincronizzare gli ordini di prestazioni sanitarie dal sistema RECUP.
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                </Paper>

                <Box sx={{ mt: 2 }}>
                    {/* Parametri Base */}
                    <Typography variant="h6" gutterBottom>
                        Parametri Principali
                    </Typography>
                    
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Metodo di Sincronizzazione</InputLabel>
                                <Select
                                    value={options.method}
                                    label="Metodo di Sincronizzazione"
                                    onChange={(e) => handleOptionChange('method', e.target.value)}
                                >
                                    <MenuItem value="all">Tutti gli appuntamenti</MenuItem>
                                    <MenuItem value="diary">Per agenda specifica (Raccomandato)</MenuItem>
                                    <MenuItem value="prescription">Per prescrizioni</MenuItem>
                                    <MenuItem value="patient">Per paziente specifico</MenuItem>
                                    <MenuItem value="specific">Appuntamenti specifici</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Timeout (millisecondi)"
                                type="number"
                                value={options.timeout}
                                onChange={(e) => handleOptionChange('timeout', Number(e.target.value))}
                                helperText="Tempo massimo di attesa (default: 10 min)"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Data Inizio"
                                type="date"
                                value={options.fromDate}
                                onChange={(e) => handleOptionChange('fromDate', e.target.value)}
                                helperText="Data di inizio per la sincronizzazione"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Data Fine"
                                type="date"
                                value={options.toDate}
                                onChange={(e) => handleOptionChange('toDate', e.target.value)}
                                helperText="Data di fine per la sincronizzazione"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Ora Inizio"
                                type="time"
                                value={options.fromTime}
                                onChange={(e) => handleOptionChange('fromTime', e.target.value)}
                                helperText="Ora di inizio (formato 24h)"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Ora Fine"
                                type="time"
                                value={options.toTime}
                                onChange={(e) => handleOptionChange('toTime', e.target.value)}
                                helperText="Ora di fine (formato 24h)"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Range di date selezionato */}
                    {options.fromDate && options.toDate && (
                        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Periodo di sincronizzazione:</strong><br/>
                                <strong>Dal:</strong> {options.fromDate} alle {options.fromTime}<br/>
                                <strong>Al:</strong> {options.toDate} alle {options.toTime}<br/>
                                <strong>Durata:</strong> {
                                    Math.ceil((new Date(`${options.toDate}T${options.toTime}:00`).getTime() - 
                                              new Date(`${options.fromDate}T${options.fromTime}:00`).getTime()) / (1000 * 60 * 60 * 24))
                                } giorni
                            </Typography>
                        </Alert>
                    )}

                    {/* Parametri Condizionali */}
                    {options.method === 'diary' && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Selezione Agende
                            </Typography>
                            
                            {loadingDiaries && (
                                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2">Caricamento agende...</Typography>
                                </Box>
                            )}
                            
                            {diariesError && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    {diariesError}
                                    <Box sx={{ mt: 1 }}>
                                        <Button 
                                            size="small" 
                                            onClick={loadDiaries} 
                                            sx={{ mr: 1 }}
                                        >
                                            Riprova
                                        </Button>
                                        {diariesError.includes('login') && (
                                            <Button 
                                                size="small" 
                                                color="primary"
                                                onClick={() => {
                                                    // Redirect al login
                                                    window.location.href = '/';
                                                }}
                                            >
                                                Vai al Login
                                            </Button>
                                        )}
                                        <Button 
                                            size="small" 
                                            color="secondary"
                                            onClick={() => {
                                                console.log('Token attuale:', localStorage.getItem('authToken') ? 'Presente' : 'Assente');
                                                console.log('Tutti i localStorage keys:', Object.keys(localStorage));
                                            }}
                                        >
                                            Debug Token
                                        </Button>
                                    </Box>
                                </Alert>
                            )}
                            
                            {!loadingDiaries && diaries.length > 0 && (
                                <FormControl fullWidth>
                                    <InputLabel>Seleziona Agende</InputLabel>
                                    <Select
                                        multiple
                                        value={selectedDiaryIds}
                                        onChange={handleDiarySelectionChange}
                                        input={<OutlinedInput label="Seleziona Agende" />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map((value) => {
                                                    const diary = diaries.find(d => d.id === value);
                                                    return (
                                                        <Chip 
                                                            key={value} 
                                                            label={diary ? `${diary.name} (${diary.execution_unit_name || 'N/A'})` : value}
                                                            size="small"
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    >
                                        {diaries.map((diary) => (
                                            <MenuItem key={diary.id} value={diary.id}>
                                                <Box>
                                                    <Typography variant="body1">
                                                        {diary.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {diary.execution_unit_name && `Unità: ${diary.execution_unit_name}`}
                                                        {diary.healthcare_service_name && ` | Servizio: ${diary.healthcare_service_name}`}
                                                        {diary.code && ` | Codice: ${diary.code}`}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            
                            {selectedDiaryIds.length > 0 && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Agende selezionate:</strong> {selectedDiaryIds.length}
                                        <br />
                                        La sincronizzazione recupererà solo gli appuntamenti delle agende selezionate per il range di date specificato.
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* Parametri specifici per Orders API */}
                    {activeTab === 1 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Parametri Specifici per Ordini
                            </Typography>
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Stato Ordine"
                                        value={options.orderStatus || ''}
                                        onChange={(e) => handleOptionChange('orderStatus', e.target.value)}
                                        placeholder="PENDING, CONFIRMED, COMPLETED"
                                        helperText="Filtra per stato specifico"
                                    />
                                </Grid>
                                
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Tipo Servizio"
                                        value={options.serviceType || ''}
                                        onChange={(e) => handleOptionChange('serviceType', e.target.value)}
                                        placeholder="LABORATORY, IMAGING, VISIT"
                                        helperText="Filtra per tipo prestazione"
                                    />
                                </Grid>
                                
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="ID Unità Operativa"
                                        value={options.executionUnitId || ''}
                                        onChange={(e) => handleOptionChange('executionUnitId', e.target.value)}
                                        placeholder="12345"
                                        helperText="Filtra per unità specifica"
                                    />
                                </Grid>
                            </Grid>
                            
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong>Nota:</strong> I parametri degli ordini sono opzionali. 
                                    Se non specificati, verranno recuperati tutti gli ordini nel range di date selezionato.
                                </Typography>
                            </Alert>
                        </Box>
                    )}

                    {options.method === 'patient' && (
                        <Box sx={{ mt: 3 }}>
                            <TextField
                                fullWidth
                                label="Codici Fiscali Pazienti (separati da virgola)"
                                multiline
                                rows={2}
                                value={patientIdsText}
                                onChange={(e) => setPatientIdsText(e.target.value)}
                                placeholder="RSSMRA80A01H501U, BNCGVN85M15F205Z"
                                helperText="Inserire i codici fiscali separati da virgola"
                            />
                        </Box>
                    )}

                    {options.method === 'specific' && (
                        <Box sx={{ mt: 3 }}>
                            <TextField
                                fullWidth
                                label="ID Prenotazioni (separati da virgola)"
                                multiline
                                rows={2}
                                value={bookingIdsText}
                                onChange={(e) => setBookingIdsText(e.target.value)}
                                placeholder="12345, 67890, 11223"
                                helperText="Inserire gli ID delle prenotazioni separati da virgola"
                            />
                        </Box>
                    )}

                    {/* Parametri Avanzati */}
                    <Box sx={{ mt: 3 }}>
                        <Button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            variant="text"
                        >
                            Parametri Avanzati
                        </Button>
                        
                        <Collapse in={showAdvanced}>
                            <Box sx={{ mt: 2 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Dimensione Batch"
                                            type="number"
                                            value={options.batchSize}
                                            onChange={(e) => handleOptionChange('batchSize', Number(e.target.value))}
                                            helperText="Numero di record per richiesta"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Tentativi Massimi"
                                            type="number"
                                            value={options.maxRetries}
                                            onChange={(e) => handleOptionChange('maxRetries', Number(e.target.value))}
                                            helperText="Numero di retry in caso di errore"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Ritardo Retry (ms)"
                                            type="number"
                                            value={options.retryDelay}
                                            onChange={(e) => handleOptionChange('retryDelay', Number(e.target.value))}
                                            helperText="Tempo di attesa tra i tentativi"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="ID Autorità"
                                            value={options.authorityId || ''}
                                            onChange={(e) => handleOptionChange('authorityId', e.target.value)}
                                            helperText="ID della struttura sanitaria"
                                            placeholder="202011400"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Box>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={options.forceSync || false}
                                                        onChange={(e) => handleOptionChange('forceSync', e.target.checked)}
                                                    />
                                                }
                                                label="Forza Sincronizzazione"
                                            />
                                            <Tooltip title="Ignora i controlli di sicurezza e duplicati">
                                                <IconButton size="small">
                                                    <InfoIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        
                                        <Box sx={{ mt: 1 }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={options.dryRun || false}
                                                        onChange={(e) => handleOptionChange('dryRun', e.target.checked)}
                                                    />
                                                }
                                                label="Modalità Test (Dry Run)"
                                            />
                                            <Tooltip title="Esegue il sync senza salvare i dati">
                                                <IconButton size="small">
                                                    <InfoIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Collapse>
                    </Box>

                    {/* Alert informativi */}
                    <Box sx={{ mt: 3 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>Servizio Selezionato:</strong> {options.apiEndpoint === 'orders' ? 'Orders API' : 'Bookings API'}
                            <br />
                            <strong>Endpoint:</strong> <code>/api/v3/system-apis/{options.apiEndpoint}</code>
                            <br />
                            <strong>Timeout:</strong> {Math.floor((options.timeout || 600000) / 60000)} minuti
                        </Alert>
                        
                        {options.dryRun && (
                            <Alert severity="warning">
                                <strong>Modalità Test attiva:</strong> La sincronizzazione verrà simulata senza salvare dati reali.
                            </Alert>
                        )}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={resetDefaults} color="secondary">
                    Reset Default
                </Button>
                <Button onClick={onClose}>
                    Annulla
                </Button>
                <Button 
                    onClick={handleSync} 
                    variant="contained" 
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <InfoIcon />}
                >
                    {loading ? 'Sincronizzazione...' : 'Anteprima Prenotazioni'}
                </Button>
            </DialogActions>
            
            {/* Modale di Anteprima */}
            {showPreviewDialog && previewOptions && (
                <BookingPreviewDialog
                    open={showPreviewDialog}
                    onClose={handleClosePreview}
                    onConfirm={handleConfirmSync}
                    syncOptions={previewOptions}
                    loading={loading}
                />
            )}
        </Dialog>
    );
};

export default SyncConfigDialog;